import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "../auth/AuthLayout";
import { Button, CopyField, Callout } from "@/design/components";
import authStyles from "../auth/AuthForm.module.css";
import styles from "./WelcomeScreen.module.css";

interface WelcomeState {
  orgId: string;
  orgName: string;
  email: string;
  /** Social sign-ups arrive already authenticated + provider-verified: skip the
   *  "check your inbox / sign in" steps and send the CTA to the dashboard. */
  signedIn?: boolean;
}

export function WelcomeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as WelcomeState | null;

  // The Org_ID only exists in this navigation's state. A direct visit / refresh
  // has nothing to show — send them to sign in rather than render an empty page.
  if (!state?.orgId) return <Navigate to="/login" replace />;

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Organization created</p>
        <h2 className={authStyles.title}>Welcome, {state.orgName}.</h2>
      </header>

      <div className={styles.body}>
        <p className={styles.lede}>
          This is your <strong>Org_ID</strong> — the shared secret your agents use to join your
          organization's channels. It is <strong>not</strong> your sign-in password: you'll sign in
          with your email and the password you chose at signup.
        </p>

        <CopyField label="Org_ID" value={state.orgId} multiline />

        <Callout tone="warning" title="Save this now — it's shown only once">
          For your security we never store the Org_ID in a way we can show again. Copy it into
          your password manager before you continue. You can rotate it later from settings.
        </Callout>

        {state.signedIn ? (
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                You're signed in as <strong>{state.email}</strong> — no email verification needed.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Create a channel, then run <code>/cogent map</code> in Slack with this Org_ID to
                connect your agents.
              </span>
            </li>
          </ol>
        ) : (
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                Check <strong>{state.email}</strong> and click the verification link we just sent.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Sign in with <strong>{state.email}</strong> and the password you chose — then map a
                Slack channel to a Cogent session.
              </span>
            </li>
          </ol>
        )}

        {state.signedIn ? (
          <Button size="lg" fullWidth onClick={() => navigate("/app", { replace: true })}>
            Continue to dashboard
          </Button>
        ) : (
          <Button
            size="lg"
            fullWidth
            onClick={() =>
              navigate("/login", { replace: true, state: { registered: true, email: state.email } })
            }
          >
            Continue to sign in
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
